/**
 * UserDeviceListener
 *
 * Mirrors Flutter's `_refreshUserAndDevice` (lib/presentation/screens/main/main_screen.dart:285-289)
 * + `listenToUserDeviceChanges` (lib/services/app_authentication_service.dart:612-648).
 *
 * Lifecycle is gated on `isAuthenticated && firebaseUserId`, so the effect
 * fires on every transition into the authenticated state — both app-restart
 * with an existing session AND fresh sign-in within the same session. This
 * is the parity-correct hook for the single-device-session mechanism:
 * AppInitializer only runs once per app boot, so wiring the write there
 * would miss the fresh-sign-in case (user A signs out, user B signs in:
 * user B's device id would never be written and user B's other browsers
 * would stay signed in).
 *
 * This listener performs the single-device-session mechanism in three steps:
 *   1. Runs `CheckUserDeviceTakeoverUseCase` first: if another browser
 *      claimed the session while this one was closed (local DEVICE_ID
 *      exists and differs from the remote one), signs this browser out
 *      and stops — the write/subscribe steps are skipped so we don't
 *      clobber the foreign id. This is the web-only piece (Flutter has
 *      stable per-device ids and does not need it).
 *   2. Writes this browser's device id to `user_device/{userId}` to claim
 *      the session.
 *   3. Subscribes to `user_device/{userId}` and signs this browser out
 *      when another browser claims the session live (device id mismatch).
 *
 * Renders nothing — mount alongside the other app listeners in AppRoutes.
 */
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch, RootState } from '@/presentation/store';
import { setAutoLoggedOut } from '@/presentation/store/slices/auth/authSlice';
import { CheckUserDeviceTakeoverUseCase } from '@/domain/usecases/userDevice/checkUserDeviceTakeoverUseCase';
import { SetUserDeviceUseCase } from '@/domain/usecases/userDevice/setUserDeviceUseCase';
import { SubscribeToUserDeviceChangesUseCase } from '@/domain/usecases/userDevice/subscribeToUserDeviceChangesUseCase';
import { signOutUseCase } from '@/domain/usecases/login/signOutUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';

export const UserDeviceListener = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const firebaseUserId = useSelector((s: RootState) => s.auth.firebaseUserId);
  // Gate on `!isInitializing` so we don't read SecureStorage before
  // AppInitializer / LoginPage have set it up. LoginPage dispatches
  // setFirebaseUserId (which flips isAuthenticated true) BEFORE awaiting
  // initializeSecureStorageUseCase, so the effect would otherwise race
  // storage init on fresh sign-in.
  const isInitializing = useSelector((s: RootState) => s.auth.isInitializing);

  useEffect(() => {
    if (!isAuthenticated || !firebaseUserId || isInitializing) return;

    // The startup takeover check is async, so the subscribe handoff and the
    // teardown both need a cancellation flag: if the effect unmounts before
    // the check resolves we must skip the write/subscribe, and if the
    // subscribe resolves after unmount we must immediately drop the handle.
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    const signOutAsAutoLoggedOut = async (reason: string) => {
      appLogger.info(reason, 'UserDeviceListener.signOutAsAutoLoggedOut', { context: { firebaseUserId } });
      // Set BEFORE the sign-out so LoginPage can read it on mount. The
      // `setFirebaseUserId(null)` dispatched by RootScreen on auth change
      // does not touch `autoLoggedOut`, so the flag survives the logout.
      // Mirrors Flutter's `AuthenticationNotifier.setLoggedOut(false)`
      // (`isManual=false`) → `LoginPage.autoLoggedOut=true`.
      dispatch(setAutoLoggedOut(true));
      // Use SignOutUseCase (not authService.signOut directly) so SecureStorage
      // is wiped before Firebase signOut. This clears the stale local DEVICE_ID
      // — without it, SecureStorage retains DEVICE_ID across the same-user
      // re-login (same userId derives the same encryption key), the next
      // mount's takeover check reads the stale id, finds the other browser's
      // id in Firestore, and auto-logs the user out again — an infinite loop.
      await signOutUseCase.execute();
    };

    void (async () => {
      // Startup takeover check: another browser may have claimed the session
      // while this one was closed. Must run BEFORE the write — otherwise our
      // `setUserDevice` clobbers the remote evidence and the takeover is lost.
      const takeover = await new CheckUserDeviceTakeoverUseCase().execute(firebaseUserId);
      if (cancelled) return;
      if (takeover) {
        await signOutAsAutoLoggedOut('Signed out due to device takeover detected at startup');
        return;
      }

      // Write — fire-and-forget. The use case swallows + logs its own
      // errors so a Firestore blip cannot block authenticated routes from rendering.
      void new SetUserDeviceUseCase().execute(firebaseUserId);

      // Subscribe — sign out on live device mismatch.
      // Mirrors Flutter lib/services/app_authentication_service.dart:612-648.
      const handle = await new SubscribeToUserDeviceChangesUseCase().execute(
        firebaseUserId,
        () => void signOutAsAutoLoggedOut('Signed out due to device mismatch'),
      );
      if (cancelled) {
        handle();
        return;
      }
      unsubscribe = handle;
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [dispatch, isAuthenticated, firebaseUserId, isInitializing]);

  return null;
};
